import Vue from "vue";
import dayjs from "dayjs";
import { apiGetObsClientSecurityToken } from "@/api/publicApi";
import { getCookies, setCookies } from "./cookies";

const OBS_CLIENT_PARAMS_KEY = "obs_client_params";

// 添加 MIME 类型与扩展名的映射表
const mimeToExtensionMap = {
  // 图片类型
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/gif": ["gif"],
  "image/webp": ["webp"],
  "image/bmp": ["bmp"],
  "image/svg+xml": ["svg", "svgz"],
  "image/tiff": ["tiff", "tif"],
  "image/vnd.microsoft.icon": ["ico"],
  "image/heic": ["heic"],
  "image/heif": ["heif"],

  // 视频类型
  "video/mp4": ["mp4"],
  "video/avi": ["avi"],
  "video/quicktime": ["mov"],
  "video/x-msvideo": ["avi"],
  "video/x-matroska": ["mkv"],
  "video/webm": ["webm"],
  "video/ogg": ["ogv"],
  "video/3gpp": ["3gp"],
  "video/3gpp2": ["3g2"],
  "video/h264": ["h264"],
  "video/mpeg": ["mpeg", "mpg"],

  // 音频类型
  "audio/mpeg": ["mp3"],
  "audio/ogg": ["ogg", "oga"],
  "audio/wav": ["wav"],
  "audio/webm": ["weba"],
  "audio/aac": ["aac"],
  "audio/x-wav": ["wav"],
  "audio/mp4": ["m4a"],

  // 文档类型
  "application/pdf": ["pdf"],
  "application/json": ["json"],
  "text/plain": ["txt"],
  // "text/html": ["html", "htm"],
  // "text/css": ["css"],
  // "text/csv": ["csv"],
  // "text/javascript": ["js"],
  // "application/javascript": ["js"],
  // "application/xml": ["xml"],
  // "text/xml": ["xml"],

  // Office文档类型
  "application/msword": ["doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "application/vnd.ms-excel": ["xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.ms-powerpoint": ["ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ["pptx"],

  // 压缩文件类型
  "application/zip": ["zip"],
  "application/x-rar-compressed": ["rar"],
  "application/x-7z-compressed": ["7z"],
  "application/gzip": ["gz"],
  "application/x-tar": ["tar"],

  // 其他常用类型
  "application/octet-stream": ["bin"],
  "application/x-font-ttf": ["ttf"],
  "application/font-woff": ["woff"],
  "application/font-woff2": ["woff2"],
  "application/x-font-opentype": ["otf"],
  "font/woff": ["woff"],
  "font/woff2": ["woff2"]
};

// 没有mime类型的文件扩展名
const noMimeTypeExtension = ["atlas", "skel"];
/**
 * 获取文件扩展名
 * @param {File} file - 文件对象
 * @returns {Array|null} 文件扩展名匹配结果
 */
function fnGetExtension(file) {
  const fileName = file.name;
  const patternFileExtension = /\.([0-9a-z]+)(?:[\\?#]|$)/i;
  return fileName.match(patternFileExtension);
}

/**
 * base64转File对象
 * @param {string} dataurl - base64数据URL
 * @param {string} filename - 文件名
 * @returns {File} File对象
 */
function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new File([u8arr], filename, { type: mime });
}

/**
 * 初始化获取obs链接参数
 * @returns
 */
async function initObsClientParams() {
  try {
    const res = await apiGetObsClientSecurityToken();
    if (res && res.code == 0) {
      const { access, secret, security_token, bucket_name, end_point } = res.data;
      const obsClinetParams = {
        access_key_id: access,
        secret_access_key: secret,
        security_token: security_token,
        server: end_point,
        bucket_name: bucket_name
      };
      setCookies(OBS_CLIENT_PARAMS_KEY, JSON.stringify(obsClinetParams), {
        expires: new Date(Date.now() + 60 * 60 * 1000)
      });
      return obsClinetParams;
    }

    throw new Error("上传token获取异常");
  } catch (error) {
    throw new Error("上传token获取异常");
  }
}

/**
 * 创建文件存储key
 * @param {Object} params - 参数对象
 * @param {number} w - 宽度
 * @param {number} h - 高度
 * @returns {string} 文件key
 */
function createFileKey(params, w = 0, h = 0) {
  const store = Vue.__VUEX_STORE__;
  let key = "";
  if (params.is_live2d) {
    key = `wiki2.0/live2d/`;
  } else if (params.is_video) {
    key = `wiki2.0/video/`;
  } else {
    key = `wiki2.0/images/w_${w}/h_${h}/`;
  }

  if (params.is_live2d) {
    key += store.getters.gameInfo.id + "/";
    // 区分目录，不然会重复
    key += params.live2dKey + "/";
    // 文件名
    key += params.file.name;
  } else {
    key += store.getters.gameInfo.id + "/";
    key += (getCookies("userUid") || 0) + "/";
    key += dayjs().year() + "/";
    key += dayjs().month() + "/";
    key += dayjs().date() + "/";
    key += Number.parseInt(Math.random() * 1000000);
    key += fnGetExtension(params.file)[0];
  }

  return key;
}

/**
 * 获取图片原始尺寸
 * @param {File} file - 图片文件
 * @returns {Promise<Object>} 包含宽高的对象
 */
function getImageNaturalSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    let img = new Image();
    img.setAttribute("crossOrigin", "Anonymous");
    img.setAttribute("src", url);

    img.onload = function() {
      URL.revokeObjectURL(url); // 释放内存
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = function() {
      URL.revokeObjectURL(url); // 释放内存
      reject(new Error("图片加载失败"));
    };
  });
}

/**
 * 添加水印到图片
 * @param {File} file - 图片文件
 * @param {number} waterMark - 水印类型
 * @returns {Promise<File>} 添加水印后的文件
 */
async function addWaterMark(file, waterMark) {
  // 水印类型映射
  const waterMarkUrls = {
    1: "//cdnimg01.gamekee.com/M00/14/D4/ChpCl138b0-EYRyvAAAAAB_Hh-4423.png",
    2: "//cdnimg.gamekee.com/images/www/1623222922268_76430761.png",
    3: "//cdnimg.gamekee.com/images/www/1614248230839_29642433.png"
  };

  return new Promise((resolve, reject) => {
    const url = window.URL.createObjectURL(file);
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.src = url;

    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;

      // 图片小于200*200且不是类型3则不添加水印
      if ((h < 200 || w < 200) && waterMark !== 3) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.crossOrigin = "Anonymous";
      const ctx = canvas.getContext("2d");

      canvas.width = w;
      canvas.height = h;

      ctx.drawImage(img, 0, 0, w, h);

      const waterMarkImg = new Image();
      waterMarkImg.crossOrigin = "Anonymous";

      // 确定水印URL
      let waterMarkUrl = waterMarkUrls[waterMark];

      waterMarkImg.src = waterMarkUrl;

      waterMarkImg.onload = () => {
        try {
          switch (waterMark) {
            case 1:
              // 右下角水印
              ctx.drawImage(waterMarkImg, w - waterMarkImg.naturalWidth - 20, h - waterMarkImg.naturalHeight - 20);
              break;
            case 2:
            case 3:
              // 平铺水印
              ctx.fillStyle = ctx.createPattern(waterMarkImg, "repeat");
              ctx.fillRect(0, 0, w, h);
              break;
          }

          const dataURL = canvas.toDataURL(file.type);
          URL.revokeObjectURL(url);
          resolve(dataURLtoFile(dataURL, file.name));
        } catch (e) {
          URL.revokeObjectURL(url);
          resolve(file);
        }
      };

      waterMarkImg.onerror = e => {
        URL.revokeObjectURL(url);
        reject(new Error(`水印加载失败: ${e.message}`));
      };
    };

    img.onerror = e => {
      URL.revokeObjectURL(url);
      reject(new Error(`图片加载失败: ${e.message}`));
    };
  });
}

/**
 * 校验上传文件
 * @param {Object} params - 参数对象
 * @returns {Promise<void>}
 */
async function checkUploadFile(params) {
  const file = params.file;
  const extension = fnGetExtension(file)[1] || "";
  if (!file) {
    throw new Error("请选择文件");
  }
  if (params.accept && !params.accept.includes(extension)) {
    throw new Error(`请上传符合以下格式的文件：${params.accept}`);
  }
  if (params.maxSize && file.size / 1024 > params.maxSize) {
    throw new Error(`请上传大小在${params.maxSize}KB内的文件`);
  }
  // 校验 MIME 类型是否支持且与扩展名是否匹配 或没有MIME类型文件扩展名是否支持，
  const expectedExtensions = mimeToExtensionMap[file.type];
  if (!(expectedExtensions || noMimeTypeExtension.includes(extension))) {
    throw new Error(`请上传wiki支持的文件类型`);
  }
  if (expectedExtensions && !expectedExtensions.includes(extension.toLowerCase())) {
    throw new Error(`文件扩展名与实际文件类型不匹配`);
  }
}

/**
 * 上传文件主函数
 * @param {Object} params - 上传参数
 * @param {File} params.file - 要上传的文件对象
 * @param {string} [params.accept] - 允许的文件类型，如 'png,jpg'
 * @param {number} [params.maxSize] - 文件最大大小（KB）
 * @param {number} [params.waterMark] - 水印类型（1: 右下角水印, 2/3: 平铺水印）
 * @param {boolean} [params.is_live2d] - 是否为live2d文件
 * @param {string} [params.live2dKey] - live2d文件的目录标识
 * @param {boolean} [params.is_video] - 是否为视频文件
 * @param {string} [params.key] - 自定义文件key
 * @param {Function} [params.progressCb] - 上传进度回调函数，接收(percent, {transferredAmount, totalAmount, totalSeconds})参数
 * @returns {Promise<Object>} 上传结果对象，包含key和url等信息
 */
async function uploadFile(params) {
  const store = Vue.__VUEX_STORE__;

  // 获取缓存的OBS参数或初始化
  let obsClinetParams = null;
  try {
    const cacheObsClientParams = getCookies(OBS_CLIENT_PARAMS_KEY);
    obsClinetParams = cacheObsClientParams ? JSON.parse(cacheObsClientParams) : null;
    // 没有缓存参数则初始化
    if (!obsClinetParams) {
      obsClinetParams = await initObsClientParams();
    }
  } catch (error) {
    throw new Error(`uploadFile OBS客户端参数初始化失败: ${error.message}`);
  }

  // 校验上传文件
  await checkUploadFile(params);

  // 构建OBS客户端配置
  const obsClientData = {
    // access_key_id: 'C8TAVPQ63LM4EY5AGXWL',
    // secret_access_key: 'bUlfIu1AQA1U9RJlxMMd70bzjTE02C3WazpCdlEq',
    // server: 'obs.cn-north-1.myhuaweicloud.com'
    access_key_id: obsClinetParams.access_key_id,
    secret_access_key: obsClinetParams.secret_access_key,
    security_token: obsClinetParams.security_token,
    server: obsClinetParams.server
  };
  // 设置obs桶名称
  const bucketName = obsClinetParams.bucket_name || "wiki-obs";
  // 创建obs客户端
  const obsClient = new ObsClient(obsClientData);

  // 获取文件
  let _file = params.file;
  // 创建filekey
  let objectKey = createFileKey(params);

  // 图片文件处理
  if (_file.type.startsWith("image/")) {
    try {
      const { width, height } = await getImageNaturalSize(_file);
      objectKey = createFileKey(params, width, height);
      // 图片可添加水印
      // 可添加水印的图片MIMETYPE
      const waterMarkMimeType = ["image/png", "image/jpeg", "image/webp", "image/bmp"];
      if (params.waterMark && waterMarkMimeType.includes(_file.type)) {
        _file = await addWaterMark(_file, params.waterMark);
      }
    } catch (error) {
      console.warn("uploadFile 处理图片时出错:", error.message);
    }
  }

  return new Promise((resolve, reject) => {
    obsClient.putObject(
      {
        Bucket: bucketName,
        Key: objectKey,
        SourceFile: _file,
        ProgressCallback: (transferredAmount, totalAmount, totalSeconds) => {
          // 上传进度回调
          let percent = parseInt((transferredAmount * 100.0) / totalAmount);
          params.progressCb && params.progressCb(percent, { transferredAmount, totalAmount, totalSeconds });
        }
      },
      function(err, result) {
        if (!err && result.CommonMsg.Status < 300) {
          const uploadResult = {
            ...result,
            key: objectKey,
            url: `//${store.getters.gameInfo.cdn_base_url}/${objectKey}`
          };
          console.log("上传成功", uploadResult);
          resolve(uploadResult);
        } else {
          const errorMsg = err ? err.message : result.CommonMsg.Code;
          reject(new Error(`上传失败: ${errorMsg}`));
        }
      }
    );
  });
}

/**
 * 切片上传文件主函数
 * @param {Object} params - 上传参数
 * @param {File} params.file - 要上传的文件对象
 * @param {string} [params.accept] - 允许的文件类型，如 'png,jpg'
 * @param {number} [params.maxSize] - 文件最大大小（KB）
 * @param {boolean} [params.is_live2d] - 是否为live2d文件
 * @param {string} [params.live2dKey] - live2d文件的目录标识
 * @param {boolean} [params.is_video] - 是否为视频文件
 * @param {string} [params.key] - 自定义文件key
 * @param {number} [params.maxConcurrent] - 最大并发上传数，默认为5
 * @param {Function} [params.progressCb] - 上传进度回调函数，接收(percent)参数
 * @param {Function} [params.uploadingCb] - 上传过程回调函数，可用于取消上传
 * @returns {Promise<Object>} 上传结果对象，包含key和url等信息
 */
async function uploadFileMulti(params) {
  const store = Vue.__VUEX_STORE__;

  // 复用uploadFile中的OBS客户端初始化逻辑
  let obsClinetParams = null;
  try {
    const cacheObsClientParams = getCookies(OBS_CLIENT_PARAMS_KEY);
    obsClinetParams = cacheObsClientParams ? JSON.parse(cacheObsClientParams) : null;
    if (!obsClinetParams) {
      obsClinetParams = await initObsClientParams();
    }
  } catch (error) {
    throw new Error(`uploadFileMulti OBS客户端参数初始化失败: ${error.message}`);
  }

  // 复用校验逻辑
  await checkUploadFile(params);

  // 构建OBS客户端配置
  const obsClientData = {
    access_key_id: obsClinetParams.access_key_id,
    secret_access_key: obsClinetParams.secret_access_key,
    security_token: obsClinetParams.security_token,
    server: obsClinetParams.server
  };

  const bucketName = obsClinetParams.bucket_name || "wiki-obs";
  const obsClient = new ObsClient(obsClientData);

  // 获取文件和创建filekey
  let _file = params.file;
  let objectKey = createFileKey(params);
  console.log("fileKey:", objectKey);

  const fileSize = params.file.size;
  const partSize = 1024 * 500; // 500KB per part
  const partCount = Math.ceil(fileSize / partSize);

  // 初始化分片上传
  const multipartUploadRes = await obsClient.initiateMultipartUpload({
    Bucket: bucketName,
    Key: objectKey
  });

  const uploadId = multipartUploadRes.InterfaceResult.UploadId;

  // 上传状态管理
  const uploadPartStatus = {
    bucketName,
    objectKey,
    uploadId,
    file: _file,
    fileSize,
    partSize,
    partCount,
    currentPartIndex: 0,
    parts: []
  };

  const globalContext = {
    maxTaskNum: 5,
    currentTaskNum: 0
  };

  return new Promise((resolve, reject) => {
    // 优化后的 uploadFileMulti 函数中的 doUploadPart 部分，支持设置并发数量
    const doUploadPart = async () => {
      // 获取或设置默认并发数
      const maxConcurrent = params.maxConcurrent || globalContext.maxTaskNum || 5;

      // 创建所有上传任务
      const uploadTasks = [];
      for (let i = 0; i < uploadPartStatus.partCount; i++) {
        const partNumber = i + 1;
        const offset = i * uploadPartStatus.partSize;
        const currPartSize =
          partNumber === uploadPartStatus.partCount ? uploadPartStatus.fileSize - offset : uploadPartStatus.partSize;

        uploadTasks.push({ partNumber, offset, currPartSize });
      }

      // 控制并发执行上传任务
      const results = [];
      for (let i = 0; i < uploadTasks.length; i += maxConcurrent) {
        // 检查是否取消上传
        if (params.cancelUpload) {
          return false;
        }

        // 获取当前批次的任务
        const batch = uploadTasks.slice(i, i + maxConcurrent);

        // 并发执行当前批次任务
        const batchResults = await Promise.allSettled(
          batch.map(({ partNumber, offset, currPartSize }) =>
            uploadSinglePart(obsClient, uploadPartStatus, partNumber, offset, currPartSize, params)
          )
        );

        // 合并结果
        results.push(...batchResults);

        // 更新进度
        updateProgress(uploadPartStatus);
      }

      // 检查是否有任务被取消
      if (params.cancelUpload) {
        return false;
      }

      // 检查所有任务是否成功
      const failedTasks = results.filter(result => result.status === "rejected");
      if (failedTasks.length > 0) {
        const errors = failedTasks.map(result => result.reason);
        console.error("部分上传任务失败:", errors);
        reject(new Error(`上传失败: ${errors[0]?.message || "部分任务失败"}`));
        return;
      }

      // 收集所有成功的部分信息
      const parts = results
        .map(result => result.value)
        .filter(part => part !== null)
        .sort((a, b) => a.PartNumber - b.PartNumber);

      // 完成分片上传
      try {
        const completeResult = await obsClient.completeMultipartUpload({
          Bucket: uploadPartStatus.bucketName,
          Key: uploadPartStatus.objectKey,
          UploadId: uploadPartStatus.uploadId,
          Parts: parts
        });

        if (completeResult.CommonMsg.Status < 300) {
          updateProgress(uploadPartStatus, "end");
          const finalResult = {
            ...completeResult,
            key: objectKey,
            url: `//${store.getters.gameInfo.cdn_base_url}/${objectKey}`
          };

          console.log("上传成功", finalResult);
          resolve(finalResult);
        } else {
          const errorMsg = completeResult.CommonMsg.Code;
          console.error(errorMsg);
          reject(new Error(`上传失败: ${errorMsg}`));
        }
      } catch (completeError) {
        console.error(completeError);
        reject(completeError);
      }
    };

    // 单个分片上传函数保持不变
    const uploadSinglePart = async (obsClient, uploadPartStatus, partNumber, offset, partSize, params) => {
      // 检查是否取消上传
      if (params.cancelUpload) {
        return null;
      }

      try {
        const result = await obsClient.uploadPart({
          Bucket: uploadPartStatus.bucketName,
          Key: uploadPartStatus.objectKey,
          PartNumber: partNumber,
          UploadId: uploadPartStatus.uploadId,
          SourceFile: uploadPartStatus.file,
          Offset: offset,
          PartSize: partSize
        });

        if (result.CommonMsg.Status < 300) {
          // 在这里更新 currentPartIndex
          uploadPartStatus.currentPartIndex = partNumber;
          return {
            PartNumber: partNumber,
            ETag: result.InterfaceResult.ETag
          };
        } else {
          throw new Error(`分片${partNumber}上传失败: ${result.CommonMsg.Code}`);
        }
      } catch (uploadError) {
        console.error(`分片${partNumber}上传错误:`, uploadError);
        throw uploadError;
      }
    };

    // 更新上传进度
    const updateProgress = (data, status) => {
      if (status === "end") {
        params.progressCb && params.progressCb(100);
        return;
      }

      const uploadedParts = data.currentPartIndex;
      const totalSize = data.fileSize;
      const uploadedSize = Math.min(uploadedParts * data.partSize, totalSize);
      const percent = parseFloat(((uploadedSize / totalSize) * 100).toFixed(2));

      if (!params.cancelUpload) {
        params.progressCb && params.progressCb(percent);
      }

      params.uploadingCb &&
        params.uploadingCb(cbParams => {
          if (cbParams?.cancelUpload) {
            params.cancelUpload = true;
          }
        });
    };

    // 开始上传
    doUploadPart();
  });
}

export { uploadFile, uploadFileMulti };
